// Phase 1 — interactive course map + polygon editor.
// Satellite basemap, course centroid, zone polygons, basemap toggle,
// draw mode for creating new zones, edit mode for updating geometry.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import mapboxgl, { type Map as MapboxMap, type LngLatLike } from 'mapbox-gl';
import type {
  AnomalyOverviewResponse,
  Course,
  Hole,
  PlacedSensor,
  SatelliteIndex,
  UserRole,
  Zone,
  ZoneType,
} from '@greeniq/shared';
import { api, ApiError } from '../api/client.js';
import { ZONE_TYPE_STYLE } from '../lib/zoneStyle.js';
import { attachPolygonEditor, type PolygonEditorHandle } from '../lib/polygonEditor.js';
import {
  getAnomalyOverview,
  getCourseOverview,
  getTimelapseFrames,
  type OverviewResponse,
  type TimelapseFrame,
} from '../api/satellite.js';
import { TimelapseScrubber, type TimelapseSpeed } from '../components/TimelapseScrubber.js';
import {
  prefetchNearbyFrames,
  removeTimelapse,
  renderTimelapseFrame,
} from '../components/timelapse.js';
import { getSensorPlacements, updateSensorPlacement } from '../api/sensors.js';
import { zoneHeatmapFill } from '../lib/idw.js';
import {
  colorForValue,
  colorForZScore,
  INDEX_DESCRIPTIONS,
  INDEX_META,
  INDEX_VALUE_STOPS,
  NO_DATA_COLOR,
} from '../lib/satelliteColors.js';
import { TimeScrubber } from '../components/TimeScrubber.js';
import 'mapbox-gl/dist/mapbox-gl.css';

interface CourseResponse {
  course: Course;
  holes: Hole[];
  zones: Zone[];
  role: UserRole;
}

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN ?? '';
const EDIT_ROLES: UserRole[] = ['ageye_admin', 'superintendent', 'assistant_super'];

type Basemap = 'satellite-streets' | 'satellite' | 'streets';

const BASEMAP_STYLES: Record<Basemap, string> = {
  'satellite-streets': 'mapbox://styles/mapbox/satellite-streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  streets: 'mapbox://styles/mapbox/streets-v12',
};

const ZONE_TYPE_OPTIONS: ZoneType[] = [
  'green',
  'tee_complex',
  'fairway',
  'rough',
  'bunker',
  'cart_path',
  'practice',
  'nursery',
  'maintenance',
  'water_feature',
];

type SatelliteView = 'absolute' | 'anomaly';

type SatelliteModeState =
  | { active: false }
  | {
      active: true;
      view: SatelliteView;
      index: SatelliteIndex;
      date: string;
      overview: OverviewResponse;
      anomaly: AnomalyOverviewResponse | null;
    };


export function CourseMapPage() {
  const { courseSlug } = useParams();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const editorRef = useRef<PolygonEditorHandle | null>(null);
  // Pulse-ring HTML markers for stressed sensors. Kept in a ref so the
  // lifecycle effect can cleanly remove stale markers before each render.
  const pulseMarkersRef = useRef<mapboxgl.Marker[]>([]);
  // Flips true when map's `load` event fires — guarantees style + glyphs +
  // base layers are all ready. Render effects gate on this to avoid the race
  // where a `once('styledata')` closure captures stale data and later wipes
  // a freshly-rendered layer via setData([]).
  const [mapReady, setMapReady] = useState(false);
  const [data, setData] = useState<CourseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [basemap, setBasemap] = useState<Basemap>('satellite-streets');
  const [activeZoneId, setActiveZoneId] = useState<number | null>(null);

  const [satelliteMode, setSatelliteMode] = useState<SatelliteModeState>({ active: false });
  const [satelliteError, setSatelliteError] = useState<string | null>(null);
  const [satellitePlaying, setSatellitePlaying] = useState(false);
  const [satelliteSpeed, setSatelliteSpeed] = useState<0.5 | 1 | 2>(1);
  const [sensorPlacements, setSensorPlacements] = useState<PlacedSensor[]>([]);
  const [showSensors, setShowSensors] = useState(true);
  const [showZones, setShowZones] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);

  // Satellite timelapse (5-year bi-weekly true-color imagery). Parent owns
  // the state so the Mapbox layer controller and the scrubber UI share one
  // source of truth; auto-advance is driven by a setInterval owned here.
  const [timelapseActive, setTimelapseActive] = useState(false);
  const [timelapseFrames, setTimelapseFrames] = useState<TimelapseFrame[]>([]);
  const [timelapseIndex, setTimelapseIndex] = useState(0);
  const [timelapsePlaying, setTimelapsePlaying] = useState(false);
  const [timelapseSpeed, setTimelapseSpeed] = useState<TimelapseSpeed>(2);
  const [timelapseError, setTimelapseError] = useState<string | null>(null);
  const [moveSensorId, setMoveSensorId] = useState<number | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [selectedSensorId, setSelectedSensorId] = useState<number | null>(null);

  // Draft-polygon state for create / edit.
  const [editing, setEditing] = useState<
    | null
    | { mode: 'create'; polygon: GeoJSON.Polygon | GeoJSON.MultiPolygon | null }
    | { mode: 'edit'; zoneId: number; polygon: GeoJSON.Polygon | GeoJSON.MultiPolygon | null }
  >(null);

  const canEdit = !!data && EDIT_ROLES.includes(data.role);
  const isAdmin = data?.role === 'ageye_admin';
  const [osmImport, setOsmImport] = useState<OsmImportState>({ status: 'idle' });

  useEffect(() => {
    if (!courseSlug) return;
    void reload();
  }, [courseSlug]);

  async function reload() {
    try {
      const resp = await api.get<CourseResponse>(`/courses/${courseSlug}`);
      setData(resp);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  // Sensor placements: fetched on mount + refreshed every 60s so the map
  // picks up new ticks without a full page reload. Cheap — one query.
  useEffect(() => {
    if (!courseSlug) return;
    let cancelled = false;
    async function loadPlacements() {
      try {
        const r = await getSensorPlacements(courseSlug!);
        if (!cancelled) setSensorPlacements(r.placements);
      } catch {
        if (!cancelled) setSensorPlacements([]);
      }
    }
    void loadPlacements();
    const interval = setInterval(loadPlacements, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [courseSlug]);

  // Init map once per basemap/course. Data changes (zone save, delete, edit)
  // do NOT retrigger init — if they did, the map would tear down on every
  // save and reset camera/zoom/basemap back to defaults. Instead we use a
  // boolean "dataLoaded" to kick the effect on first data arrival; the
  // separate [data, fillOverride] effect below keeps zones in sync after.
  const dataLoaded = !!data;
  const initialDataRef = useRef<CourseResponse | null>(null);
  initialDataRef.current = data;

  useEffect(() => {
    if (!containerRef.current || !MAPBOX_TOKEN) return;
    if (mapRef.current) return;
    const snapshot = initialDataRef.current;
    if (!snapshot) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const { course } = snapshot;
    const center: LngLatLike = [
      Number(course.centroidLng ?? -98),
      Number(course.centroidLat ?? 39),
    ];

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: BASEMAP_STYLES[basemap],
      center,
      zoom: 15,
      pitch: 0,
    });
    mapRef.current = map;

    map.addControl(new mapboxgl.NavigationControl({ showCompass: true }), 'top-right');
    map.addControl(new mapboxgl.ScaleControl({ unit: 'imperial' }));

    map.on('load', () => {
      const latest = initialDataRef.current;
      if (latest) renderZones(map, latest.zones, setActiveZoneId, fillOverride);
      // Attach editor with a stable commit handler
      editorRef.current = attachPolygonEditor(map, (polygon) => {
        setEditing((prev) => (prev ? { ...prev, polygon } : prev));
      });
      setMapReady(true);
    });

    return () => {
      editorRef.current?.remove();
      editorRef.current = null;
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basemap, courseSlug, dataLoaded]);

  // Build the per-zone fill override when satellite mode is active.
  const satelliteFillOverride = useMemo<Record<number, string> | null>(() => {
    if (!satelliteMode.active) return null;
    const mapping: Record<number, string> = {};
    if (satelliteMode.view === 'anomaly' && satelliteMode.anomaly) {
      for (const a of satelliteMode.anomaly.anomalies) {
        mapping[a.zoneId] = colorForZScore(a.zScore);
      }
      return mapping;
    }
    for (const [zoneIdStr, dates] of Object.entries(satelliteMode.overview.byZone)) {
      const zoneId = Number(zoneIdStr);
      const cell = dates[satelliteMode.date];
      mapping[zoneId] = cell
        ? colorForValue(satelliteMode.index, cell.value)
        : NO_DATA_COLOR;
    }
    return mapping;
  }, [satelliteMode]);

  // Heatmap mode recolors zones by their most-stressed sensor reading. Using
  // the MIN VWC across a zone's probes (not the mean) so a green with a dry
  // 2" surface lights up red even when the 4" rootzone is still healthy —
  // that's the reading a superintendent wants to see first.
  const heatmapFillOverride = useMemo<Record<number, string> | null>(() => {
    if (!showHeatmap) return null;
    const minByZone = new Map<number, number>();
    for (const s of sensorPlacements) {
      if (s.zoneId === null || s.latestVwc === null) continue;
      const prev = minByZone.get(s.zoneId);
      if (prev === undefined || s.latestVwc < prev) {
        minByZone.set(s.zoneId, s.latestVwc);
      }
    }
    if (minByZone.size === 0) return null;
    const mapping: Record<number, string> = {};
    for (const [zid, minVwc] of minByZone) {
      mapping[zid] = zoneHeatmapFill(minVwc);
    }
    return mapping;
  }, [showHeatmap, sensorPlacements]);

  // Heatmap wins over satellite view when both are active so the most
  // recent toggle "rules" the visual — consistent with how the toolbar
  // buttons feel.
  const fillOverride = useMemo<Record<number, string> | null>(() => {
    if (!heatmapFillOverride && !satelliteFillOverride) return null;
    return { ...(satelliteFillOverride ?? {}), ...(heatmapFillOverride ?? {}) };
  }, [heatmapFillOverride, satelliteFillOverride]);

  // Keep zones in sync when data, overlay, or visibility changes.
  // When showZones is off, pass an empty array so the layer is still present
  // but renders nothing — avoids teardown/rebuild thrash.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !data) return;
    const zonesToRender = showZones ? data.zones : [];
    renderZones(map, zonesToRender, setActiveZoneId, fillOverride);
  }, [mapReady, data, fillOverride, showZones]);

  // Sensor dots sync — separate from zones so toggling visibility or
  // arrival of a fresh tick doesn't re-render the whole zone layer.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const visible = showSensors ? sensorPlacements : [];
    renderSensors(map, visible, moveSensorId);
  }, [mapReady, sensorPlacements, showSensors, moveSensorId]);

  // Ambient sensor-density heatmap — toggled via the Show/Hide heatmap
  // button alongside the per-zone fill override. Layer is added by
  // renderSensors at `visibility: 'none'`; we just flip the layout here.
  const hasLiveSensor = sensorPlacements.some((p) => p.latestVwc !== null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!map.getLayer(SENSOR_HEATMAP_LAYER_ID)) return;
    map.setLayoutProperty(
      SENSOR_HEATMAP_LAYER_ID,
      'visibility',
      showHeatmap && hasLiveSensor ? 'visible' : 'none',
    );
  }, [mapReady, showHeatmap, hasLiveSensor]);

  // Pulse rings on stressed (critical + warn) sensors. Uses HTML markers
  // rather than an animated Mapbox expression so CSS keyframes handle the
  // animation — zero per-frame JS cost. Only sensors below 0.13 VWC get a
  // ring; healthy + saturated stay as static dots to avoid visual noise.
  // Scales to ~15 stressed sensors without any perf concern; if a future
  // course seeds 100+ stressed sensors we'd switch to a canvas layer.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    // Wipe previous markers before each render pass.
    pulseMarkersRef.current.forEach((m) => m.remove());
    pulseMarkersRef.current = [];
    if (!showSensors) return;
    for (const p of sensorPlacements) {
      if (p.latestVwc === null || p.latestVwc >= 0.13) continue;
      const isCritical = p.latestVwc < 0.10;
      const el = document.createElement('div');
      el.className = isCritical ? 'sensor-pulse' : 'sensor-pulse sensor-pulse--warn';
      // Stagger ripple phase by sensor id so neighbors don't pulse in lockstep.
      el.style.setProperty('--pulse-delay', `${(p.id * 137) % 1800}ms`);
      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([p.lng, p.lat])
        .addTo(map);
      pulseMarkersRef.current.push(marker);
    }
  }, [mapReady, showSensors, sensorPlacements]);

  // Heatmap layer is now implemented via the zone-fill override (see
  // `heatmapFillOverride` above), not a separate IDW circle grid — the
  // previous approach bled colored blobs 40–50m past zone boundaries at
  // low zoom because Mapbox's `circle-blur` has no polygon clipping.

  // Timelapse raster sync — adds/updates an image source + raster layer
  // positioned beneath zone fills so polygons + sensor dots stay legible.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!timelapseActive || timelapseFrames.length === 0) {
      removeTimelapse(map);
      return;
    }
    const frame = timelapseFrames[timelapseIndex];
    if (!frame) return;
    renderTimelapseFrame(map, frame, 0.9, ZONE_FILL_LAYER);
    prefetchNearbyFrames(timelapseFrames, timelapseIndex);
  }, [mapReady, timelapseActive, timelapseFrames, timelapseIndex]);

  // Auto-advance — 1x = 1 frame/sec, 0.5x = 2s/frame, 2x = 0.5s/frame.
  // Auto-pauses at the last frame so playback has a clear end-state.
  useEffect(() => {
    if (!timelapseActive || !timelapsePlaying || timelapseFrames.length === 0) return;
    const periodMs = 1000 / timelapseSpeed;
    const handle = window.setInterval(() => {
      setTimelapseIndex((i) => {
        const next = i + 1;
        if (next >= timelapseFrames.length) {
          setTimelapsePlaying(false);
          return i;
        }
        return next;
      });
    }, periodMs);
    return () => window.clearInterval(handle);
  }, [timelapseActive, timelapsePlaying, timelapseSpeed, timelapseFrames.length]);

  // Sat Mode auto-advance — walks state.date forward through the observation
  // list. Auto-pauses on the last date so playback has an end-state.
  const satelliteDateCount = satelliteMode.active ? satelliteMode.overview.dates.length : 0;
  useEffect(() => {
    if (!satelliteMode.active || !satellitePlaying || satelliteDateCount === 0) return;
    const periodMs = 1000 / satelliteSpeed;
    const handle = window.setInterval(() => {
      setSatelliteMode((prev) => {
        if (!prev.active) return prev;
        const dates = prev.overview.dates;
        const idx = dates.indexOf(prev.date);
        const nextIdx = idx + 1;
        if (nextIdx >= dates.length) {
          setSatellitePlaying(false);
          return prev;
        }
        return { ...prev, date: dates[nextIdx]! };
      });
    }, periodMs);
    return () => window.clearInterval(handle);
  }, [satelliteMode.active, satellitePlaying, satelliteSpeed, satelliteDateCount]);

  async function toggleTimelapse() {
    if (timelapseActive) {
      setTimelapseActive(false);
      setTimelapsePlaying(false);
      return;
    }
    if (!courseSlug) return;
    try {
      setTimelapseError(null);
      const { frames } = timelapseFrames.length > 0
        ? { frames: timelapseFrames }
        : await getTimelapseFrames(courseSlug);
      if (frames.length === 0) {
        setTimelapseError('No timelapse frames yet — run the admin backfill first.');
        return;
      }
      if (timelapseFrames.length === 0) setTimelapseFrames(frames);
      setTimelapseIndex((i) =>
        i < frames.length ? i : frames.length - 1,
      );
      // Sat Mode + Timelapse share the bottom scrubber slot and each express
      // "scrub dates for this layer", so they're mutually exclusive. Turning
      // one on auto-exits the other.
      if (satelliteMode.active) {
        setSatelliteMode({ active: false });
        setSatelliteError(null);
        setSatellitePlaying(false);
      }
      setTimelapseActive(true);
    } catch (err) {
      setTimelapseError(err instanceof ApiError ? err.message : 'Failed to load frames');
    }
  }

  // Move-mode click handler — when moveSensorId is set, the next click on
  // the map commits the PATCH. Guard with a short deferred (60ms) so the
  // click that ENTERED move mode doesn't immediately trigger the commit.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !courseSlug || moveSensorId === null) return;
    let armed = false;
    const arm = setTimeout(() => { armed = true; }, 60);
    function handleClick(e: mapboxgl.MapMouseEvent) {
      if (!armed) return;
      const lng = e.lngLat.lng;
      const lat = e.lngLat.lat;
      void (async () => {
        try {
          await updateSensorPlacement(courseSlug!, moveSensorId!, lng, lat);
          setSensorPlacements((prev) =>
            prev.map((p) => (p.id === moveSensorId ? { ...p, lng, lat } : p)),
          );
          setMoveError(null);
        } catch (err) {
          setMoveError(err instanceof ApiError ? err.message : String(err));
        } finally {
          setMoveSensorId(null);
          setSelectedSensorId(null);
        }
      })();
    }
    map.getCanvas().style.cursor = 'crosshair';
    map.on('click', handleClick);
    return () => {
      clearTimeout(arm);
      map.off('click', handleClick);
      map.getCanvas().style.cursor = '';
    };
  }, [moveSensorId, courseSlug]);

  // Sensor click → select for info panel (not in move mode).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    function handleSensorClick(e: mapboxgl.MapMouseEvent & { features?: mapboxgl.GeoJSONFeature[] }) {
      if (moveSensorId !== null) return;
      const feat = e.features?.[0];
      if (!feat) return;
      const rawId = (feat.properties as { id?: unknown }).id;
      const id = typeof rawId === 'number' ? rawId : Number(rawId);
      if (Number.isFinite(id)) {
        setSelectedSensorId(id);
        e.originalEvent.stopPropagation();
      }
    }
    map.on('click', SENSOR_LAYER_ID, handleSensorClick);
    return () => {
      map.off('click', SENSOR_LAYER_ID, handleSensorClick);
    };
  }, [moveSensorId]);

  async function enableSatelliteMode(index: SatelliteIndex) {
    if (!courseSlug) return;
    setSatelliteError(null);
    try {
      const overview = await getCourseOverview(courseSlug, index);
      if (overview.dates.length === 0) {
        setSatelliteError('No satellite observations yet. Trigger refresh on a zone, or wait for the next cron.');
        return;
      }
      // Mutually exclusive with Timelapse — see toggleTimelapse for the
      // matching side of the handshake.
      if (timelapseActive) {
        setTimelapseActive(false);
        setTimelapsePlaying(false);
      }
      setSatelliteMode({
        active: true,
        view: 'absolute',
        index,
        date: overview.dates[overview.dates.length - 1]!,
        overview,
        anomaly: null,
      });
    } catch (err) {
      setSatelliteError(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function switchSatelliteIndex(index: SatelliteIndex) {
    if (!courseSlug) return;
    try {
      const overview = await getCourseOverview(courseSlug, index);
      // If anomaly view is active, refetch anomaly for the new index too.
      let anomaly: AnomalyOverviewResponse | null = null;
      setSatelliteMode((prev) => {
        if (prev.active && prev.view === 'anomaly') {
          // Kick off async fetch; UI shows stale colors until it resolves.
          void (async () => {
            try {
              const a = await getAnomalyOverview(courseSlug, index, prev.date);
              setSatelliteMode((cur) => (cur.active ? { ...cur, anomaly: a } : cur));
            } catch {
              /* silent; banner logic covered in the enable toggle */
            }
          })();
        }
        return prev;
      });
      setSatelliteMode((prev) => {
        if (!prev.active) return prev;
        const nextDate = overview.dates.includes(prev.date)
          ? prev.date
          : overview.dates[overview.dates.length - 1] ?? prev.date;
        return { ...prev, index, date: nextDate, overview, anomaly };
      });
    } catch (err) {
      setSatelliteError(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function switchSatelliteView(view: SatelliteView) {
    if (!courseSlug) return;
    setSatelliteMode((prev) => (prev.active ? { ...prev, view } : prev));
    if (view === 'anomaly') {
      const prev = satelliteMode;
      if (!prev.active) return;
      try {
        const anomaly = await getAnomalyOverview(courseSlug, prev.index, prev.date);
        setSatelliteMode((cur) => (cur.active ? { ...cur, anomaly } : cur));
      } catch (err) {
        setSatelliteError(err instanceof ApiError ? err.message : String(err));
      }
    }
  }

  function disableSatelliteMode() {
    setSatelliteMode({ active: false });
    setSatelliteError(null);
    setSatellitePlaying(false);
  }

  function startCreate() {
    setActiveZoneId(null);
    setEditing({ mode: 'create', polygon: null });
    editorRef.current?.start();
  }

  function startEdit(zone: Zone) {
    setActiveZoneId(null);
    setEditing({ mode: 'edit', zoneId: zone.id, polygon: zone.polygon });
    editorRef.current?.start(zone.polygon);
  }

  function cancelEdit() {
    editorRef.current?.cancel();
    setEditing(null);
  }

  async function saveCreate(input: { name: string; zoneType: ZoneType; grassSpecies: string }) {
    if (!editing || editing.mode !== 'create' || !editing.polygon) return;
    try {
      await api.post<{ zone: Zone }>(`/courses/${courseSlug}/zones`, {
        name: input.name,
        zoneType: input.zoneType,
        polygon: editing.polygon,
        grassSpecies: input.grassSpecies || null,
      });
      cancelEdit();
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function saveEdit() {
    if (!editing || editing.mode !== 'edit' || !editing.polygon) return;
    try {
      await api.patch<{ zone: Zone }>(`/courses/${courseSlug}/zones/${editing.zoneId}`, {
        polygon: editing.polygon,
      });
      cancelEdit();
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function deleteZone(zoneId: number) {
    if (!confirm('Delete this zone? This cannot be undone.')) return;
    try {
      await api.delete<void>(`/courses/${courseSlug}/zones/${zoneId}`);
      setActiveZoneId(null);
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    }
  }

  // Inline rename + re-link to a different hole. OSM sometimes mislabels
  // fairways (e.g., "Fairway 11" physically sits on hole 5); this UI lets
  // the super correct both the display name and the hole linkage in one
  // shot without leaving the course map.
  const [renameState, setRenameState] = useState<
    | null
    | { zoneId: number; name: string; holeId: number | null }
  >(null);

  function startRename(zone: Zone) {
    setRenameState({
      zoneId: zone.id,
      name: zone.name,
      holeId: zone.holeId ?? null,
    });
  }

  async function saveRename() {
    if (!renameState) return;
    try {
      await api.patch<unknown>(`/courses/${courseSlug}/zones/${renameState.zoneId}`, {
        name: renameState.name,
        holeId: renameState.holeId,
      });
      setRenameState(null);
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    }
  }

  if (error) return <div className="p-8 text-[color:var(--color-error)]">{error}</div>;
  if (!data) return <div className="p-8 text-fg-muted">Loading map…</div>;
  if (!MAPBOX_TOKEN) {
    return (
      <div className="p-8 text-fg-secondary">
        Mapbox token missing. Set <code className="font-mono text-sm">VITE_MAPBOX_TOKEN</code> to
        use the course map.
      </div>
    );
  }

  const { course, zones } = data;
  const activeZone = activeZoneId ? zones.find((z) => z.id === activeZoneId) ?? null : null;

  const heatmapDisabled = sensorPlacements.filter((p) => p.latestVwc !== null).length === 0;
  const totalAcres = (zones.reduce((s, z) => s + Number(z.areaSqft), 0) / 43560).toFixed(1);

  return (
    <div className="relative h-[calc(100vh-4rem)] flex flex-col">
      {/* Single top toolbar — all view/layer actions aligned together */}
      <MapToolbar
        courseSlug={courseSlug ?? ''}
        courseName={course.name}
        zoneCount={zones.length}
        totalAcres={totalAcres}
        basemap={basemap}
        onBasemapChange={setBasemap}
        showZones={showZones}
        onToggleZones={() => setShowZones((v) => !v)}
        sensorCount={sensorPlacements.length}
        showSensors={showSensors}
        onToggleSensors={() => setShowSensors((v) => !v)}
        showHeatmap={showHeatmap}
        heatmapDisabled={heatmapDisabled}
        onToggleHeatmap={() => setShowHeatmap((v) => !v)}
        satelliteActive={satelliteMode.active}
        onToggleSatelliteMode={() =>
          satelliteMode.active ? disableSatelliteMode() : void enableSatelliteMode('ndvi')
        }
        timelapseActive={timelapseActive}
        onToggleTimelapse={() => void toggleTimelapse()}
      />

      {/* Sat Mode errors only — mode controls are now inside the bottom
          scrubber card (see SatelliteScrubberOverlay) so the top-right
          corner stays reserved for passive legends. */}
      {satelliteError && (
        <div className="absolute top-24 left-1/2 -translate-x-1/2 z-20 text-xs px-3 py-2 rounded-md bg-bg-card shadow-md border border-[color:var(--color-error)]/30 text-[color:var(--color-error)] max-w-md">
          {satelliteError}
        </div>
      )}

      {moveSensorId !== null && (
        <div className="absolute top-24 left-1/2 -translate-x-1/2 z-20 bg-[#1e3a8a] text-white rounded-md shadow-md px-4 py-2 text-sm flex items-center gap-3">
          <span>📍 Click anywhere to place <strong>Sensor #{moveSensorId}</strong></span>
          <button
            onClick={() => setMoveSensorId(null)}
            className="text-xs underline"
          >
            Cancel
          </button>
        </div>
      )}
      {moveError && (
        <div className="absolute top-36 left-1/2 -translate-x-1/2 z-20 bg-[color:var(--color-error)] text-white rounded-md shadow-md px-3 py-1.5 text-xs">
          {moveError}
        </div>
      )}
      {selectedSensorId !== null && moveSensorId === null && (() => {
        const sensor = sensorPlacements.find((s) => s.id === selectedSensorId);
        if (!sensor) return null;
        return (
          <div className="absolute bottom-4 left-4 right-4 md:right-auto md:w-80 z-10 bg-bg-card rounded-lg shadow-md border border-border p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="uppercase tracking-wider text-xs text-greeniq-400 font-semibold">
                  Sensor #{sensor.id}
                </p>
                <h2 className="mt-1 text-lg font-semibold text-fg">
                  {sensor.type === 'multi_probe' ? `${sensor.depthInches ?? '?'}″ multi-probe` : 'Leaf wetness'}
                </h2>
                <p className="text-sm text-fg-muted mt-1">
                  {sensor.latestVwc !== null
                    ? `VWC ${sensor.latestVwc.toFixed(3)}`
                    : 'Awaiting first tick'}
                </p>
              </div>
              <button
                onClick={() => setSelectedSensorId(null)}
                className="text-fg-muted hover:text-fg"
                aria-label="Dismiss"
              >×</button>
            </div>
            {canEdit && (
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={() => setMoveSensorId(sensor.id)}
                  className="px-3 py-1.5 text-sm rounded-md bg-greeniq-500 text-[#0b1014] font-semibold hover:bg-greeniq-400"
                >
                  Move sensor
                </button>
                {sensor.zoneId && (
                  <Link
                    to={`/app/${courseSlug}/zones/${sensor.zoneId}`}
                    className="px-3 py-1.5 text-sm text-greeniq-400 hover:underline"
                  >
                    Open zone →
                  </Link>
                )}
              </div>
            )}
          </div>
        );
      })()}

      <div ref={containerRef} className="flex-1 w-full bg-bg-elevated" />

      {satelliteMode.active && (
        <SatelliteScrubberOverlay
          state={satelliteMode}
          playing={satellitePlaying}
          speed={satelliteSpeed}
          onDateChange={(date) =>
            setSatelliteMode((prev) => (prev.active ? { ...prev, date } : prev))
          }
          onPlayingChange={setSatellitePlaying}
          onSpeedChange={setSatelliteSpeed}
          onSelectIndex={(idx) => void switchSatelliteIndex(idx)}
          onSelectView={(v) => void switchSatelliteView(v)}
        />
      )}

      {showHeatmap && !heatmapDisabled && (
        <div className="absolute top-24 right-4 z-10 bg-bg-card border border-border rounded-md shadow-lg px-3 py-2 text-xs font-mono w-52">
          <div className="text-[10px] uppercase tracking-wider text-greeniq-400 font-display font-semibold mb-1.5">
            Moisture · zone min
          </div>
          <ul className="space-y-1 mb-1.5">
            <li className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#ef4444' }} />
              <span className="text-fg-muted">critical &lt; 10%</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#f59e0b' }} />
              <span className="text-fg-muted">warn &lt; 13%</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#22c55e' }} />
              <span className="text-fg-muted">healthy</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#3b82f6' }} />
              <span className="text-fg-muted">saturated &gt; 35%</span>
            </li>
          </ul>
          <p className="text-[10px] text-fg-muted leading-snug border-t border-border pt-1.5">
            Zones tinted by their most-stressed probe so a dry 2″ surface lights up even when the rootzone is fine.
          </p>
        </div>
      )}

      {timelapseError && (
        <div className="absolute top-24 left-1/2 -translate-x-1/2 z-20 bg-error text-white rounded-md shadow-md px-3 py-1.5 text-xs">
          {timelapseError}
        </div>
      )}
      {timelapseActive && timelapseFrames.length > 0 && (
        <TimelapseScrubber
          frames={timelapseFrames}
          currentIndex={timelapseIndex}
          playing={timelapsePlaying}
          speed={timelapseSpeed}
          onIndexChange={setTimelapseIndex}
          onPlayingChange={setTimelapsePlaying}
          onSpeedChange={setTimelapseSpeed}
          onClose={() => {
            setTimelapseActive(false);
            setTimelapsePlaying(false);
          }}
        />
      )}

      {canEdit && !editing && (
        <div className="absolute bottom-4 right-4 z-10">
          <button
            onClick={startCreate}
            className="px-4 py-3 rounded-full shadow-lg bg-greeniq-500 text-[#0b1014] font-semibold text-sm hover:bg-greeniq-400"
          >
            + Trace zone
          </button>
        </div>
      )}

      {activeZone && !editing && (
        <div className="absolute bottom-4 left-4 right-4 md:right-auto md:w-96 z-10 bg-bg-card rounded-lg shadow-md border border-border p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="uppercase tracking-wider text-xs text-greeniq-400 font-semibold">
                {activeZone.zoneType.replace(/_/g, ' ')}
              </p>
              <h2 className="mt-1 text-lg font-semibold text-fg">{activeZone.name}</h2>
              <p className="text-sm text-fg-muted mt-1">
                {Math.round(Number(activeZone.areaSqft)).toLocaleString()} sqft
                {activeZone.grassSpecies ? ` · ${activeZone.grassSpecies}` : ''}
              </p>
            </div>
            <button
              className="text-fg-muted hover:text-fg"
              onClick={() => setActiveZoneId(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
          <div className="mt-4 flex items-center gap-3 text-sm">
            <Link
              to={`/app/${courseSlug}/zones/${activeZone.id}`}
              className="text-greeniq-400 font-semibold hover:underline"
            >
              Open detail →
            </Link>
            {canEdit && (
              <>
                <button
                  onClick={() => startRename(activeZone)}
                  className="ml-auto text-fg-secondary hover:text-fg"
                >
                  Rename
                </button>
                <button
                  onClick={() => startEdit(activeZone)}
                  className="text-fg-secondary hover:text-fg"
                >
                  Edit shape
                </button>
                <button
                  onClick={() => deleteZone(activeZone.id)}
                  className="text-[color:var(--color-error)] hover:text-[color:var(--color-error)]"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {renameState && (
        <div className="absolute bottom-4 left-4 right-4 md:right-auto md:w-96 z-10 bg-bg-card rounded-lg shadow-md border border-border p-4">
          <h2 className="font-semibold text-fg">Rename / re-link zone</h2>
          <p className="text-sm text-fg-muted mt-1">
            Correct the name and the hole it belongs to. Activate-demo will pick
            up the new linkage on its next run.
          </p>
          <label className="mt-3 block text-xs uppercase tracking-wider text-fg-muted">
            Name
          </label>
          <input
            type="text"
            value={renameState.name}
            onChange={(e) => setRenameState({ ...renameState, name: e.target.value })}
            className="mt-1 w-full rounded-md border border-border bg-bg-subtle px-3 py-2 text-sm text-fg focus:border-greeniq-500 focus:outline-none"
            maxLength={80}
          />
          <label className="mt-3 block text-xs uppercase tracking-wider text-fg-muted">
            Hole
          </label>
          <select
            value={renameState.holeId ?? ''}
            onChange={(e) =>
              setRenameState({
                ...renameState,
                holeId: e.target.value === '' ? null : Number(e.target.value),
              })
            }
            className="mt-1 w-full rounded-md border border-border bg-bg-subtle px-3 py-2 text-sm text-fg focus:border-greeniq-500 focus:outline-none"
          >
            <option value="">— Unlinked —</option>
            {data.holes
              .slice()
              .sort((a, b) => a.holeNumber - b.holeNumber)
              .map((h) => (
                <option key={h.id} value={h.id}>
                  Hole {h.holeNumber}
                  {h.par ? ` · Par ${h.par}` : ''}
                </option>
              ))}
          </select>
          <div className="mt-4 flex items-center justify-end gap-3 text-sm">
            <button
              onClick={() => setRenameState(null)}
              className="text-fg-secondary hover:text-fg"
            >
              Cancel
            </button>
            <button
              onClick={() => void saveRename()}
              className="rounded-md bg-greeniq-500 px-3 py-1.5 font-semibold text-[#0b1014] hover:bg-greeniq-400"
              disabled={renameState.name.trim().length === 0}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {editing && editing.mode === 'create' && (
        <CreateZonePanel
          polygon={editing.polygon}
          onSave={saveCreate}
          onCancel={cancelEdit}
          holes={data.holes}
        />
      )}

      {editing && editing.mode === 'edit' && (
        <div className="absolute bottom-4 left-4 right-4 md:right-auto md:w-96 z-10 bg-bg-card rounded-lg shadow-md border border-border p-4">
          <h2 className="font-semibold text-fg">Editing zone shape</h2>
          <p className="text-sm text-fg-muted mt-1">
            Drag vertices to adjust. Click Save when the outline matches the zone.
          </p>
          <div className="mt-4 flex items-center gap-3 text-sm">
            <button
              onClick={saveEdit}
              disabled={!editing.polygon}
              className="px-4 py-2 rounded-md bg-greeniq-500 text-[#0b1014] font-semibold hover:bg-greeniq-400 disabled:opacity-50"
            >
              Save
            </button>
            <button onClick={cancelEdit} className="text-fg-secondary hover:text-fg">
              Cancel
            </button>
          </div>
        </div>
      )}

      {zones.length === 0 && !editing && (
        <div className="absolute inset-x-4 bottom-4 md:inset-auto md:top-24 md:right-4 md:w-80 z-10 bg-bg-card rounded-lg shadow-md border border-border p-4 text-sm">
          <p className="font-semibold text-fg">No zones traced yet.</p>
          <p className="mt-2 text-fg-muted">
            {canEdit
              ? 'Tap “Trace zone” to draw your first polygon against the satellite basemap.'
              : 'Ask your superintendent to trace the course zones from this view.'}
          </p>
          {isAdmin && (
            <button
              onClick={() => void startOsmPreview(courseSlug!, setOsmImport)}
              className="mt-3 w-full px-3 py-2 text-xs rounded-md border border-border-strong text-greeniq-300 font-semibold hover:bg-bg-hover"
            >
              Import from OpenStreetMap →
            </button>
          )}
        </div>
      )}

      {isAdmin && zones.length > 0 && !editing && (
        <div className="absolute bottom-20 right-4 z-10">
          <button
            onClick={() => void startOsmPreview(courseSlug!, setOsmImport)}
            className="px-3 py-2 text-xs rounded-md border border-border-strong bg-bg-card text-greeniq-300 font-semibold shadow-md hover:bg-bg-hover"
          >
            Import from OSM
          </button>
        </div>
      )}

      {osmImport.status !== 'idle' && (
        <OsmImportModal
          state={osmImport}
          onCommit={() => void commitOsmImport(courseSlug!, setOsmImport, () => void reload())}
          onClose={() => setOsmImport({ status: 'idle' })}
        />
      )}
    </div>
  );
}

// =========================================================================
// OSM import modal — admin-only path that pulls golf features from
// OpenStreetMap via the server's /import-osm endpoint.
// =========================================================================

type OsmImportState =
  | { status: 'idle' }
  | { status: 'loading'; phase: 'preview' | 'commit' }
  | { status: 'preview'; preview: OsmImportPreview }
  | { status: 'done'; result: OsmImportResult }
  | { status: 'error'; message: string };

interface OsmImportPreview {
  totalFeatures: number;
  byZoneType: Record<string, number>;
  holes: number[];
  features: Array<{
    osmId: number;
    zoneType: ZoneType;
    name: string;
    holeNumber: number | null;
    areaSqft: number;
    alreadyImported: boolean;
  }>;
}

interface OsmImportResult extends OsmImportPreview {
  holesInserted: number;
  zonesInserted: number;
  zonesSkipped: number;
}

async function startOsmPreview(
  courseSlug: string,
  setState: (s: OsmImportState) => void,
) {
  setState({ status: 'loading', phase: 'preview' });
  try {
    const resp = await api.post<OsmImportPreview>(`/courses/${courseSlug}/import-osm`, { mode: 'preview' });
    setState({ status: 'preview', preview: resp });
  } catch (err) {
    setState({ status: 'error', message: err instanceof ApiError ? err.message : String(err) });
  }
}

async function commitOsmImport(
  courseSlug: string,
  setState: (s: OsmImportState) => void,
  onReload: () => void,
) {
  setState({ status: 'loading', phase: 'commit' });
  try {
    const resp = await api.post<OsmImportResult>(`/courses/${courseSlug}/import-osm`, { mode: 'commit' });
    setState({ status: 'done', result: resp });
    onReload();
  } catch (err) {
    setState({ status: 'error', message: err instanceof ApiError ? err.message : String(err) });
  }
}

function OsmImportModal({
  state,
  onCommit,
  onClose,
}: {
  state: OsmImportState;
  onCommit: () => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-bg-card rounded-lg shadow-xl max-w-lg w-full p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-fg">Import from OpenStreetMap</h2>
            <p className="text-sm text-fg-muted mt-1">
              Pulls golf features (greens, tees, fairways, bunkers) traced by the OSM community for this course.
            </p>
          </div>
          <button onClick={onClose} className="text-fg-muted hover:text-fg" aria-label="Close">×</button>
        </div>

        <div className="mt-4 text-sm">
          {state.status === 'loading' && (
            <p className="text-fg-muted">
              {state.phase === 'preview' ? 'Querying Overpass API…' : 'Importing zones…'}
            </p>
          )}
          {state.status === 'error' && (
            <p className="text-[color:var(--color-error)]">Error: {state.message}</p>
          )}
          {state.status === 'preview' && <OsmImportSummary data={state.preview} />}
          {state.status === 'done' && (
            <>
              <p className="text-greeniq-300 font-semibold">
                ✓ Imported {state.result.zonesInserted} zone{state.result.zonesInserted === 1 ? '' : 's'} across {state.result.holesInserted ? state.result.holesInserted : state.result.holes.length} hole{state.result.holes.length === 1 ? '' : 's'}.
              </p>
              {state.result.zonesSkipped > 0 && (
                <p className="text-fg-muted mt-1">{state.result.zonesSkipped} already imported — skipped.</p>
              )}
            </>
          )}
        </div>

        <div className="mt-5 flex items-center justify-end gap-3">
          {state.status === 'preview' && (
            <>
              <button onClick={onClose} className="text-sm text-fg-secondary hover:text-fg">Cancel</button>
              <button
                onClick={onCommit}
                disabled={state.preview.features.every((f) => f.alreadyImported)}
                className="px-4 py-2 rounded-md bg-greeniq-500 text-[#0b1014] text-sm font-semibold hover:bg-greeniq-400 disabled:opacity-50"
              >
                Import {state.preview.features.filter((f) => !f.alreadyImported).length} new zone{state.preview.features.filter((f) => !f.alreadyImported).length === 1 ? '' : 's'}
              </button>
            </>
          )}
          {(state.status === 'done' || state.status === 'error') && (
            <button onClick={onClose} className="px-4 py-2 rounded-md bg-greeniq-500 text-[#0b1014] text-sm font-semibold hover:bg-greeniq-400">
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function OsmImportSummary({ data }: { data: OsmImportPreview }) {
  const pending = data.features.filter((f) => !f.alreadyImported).length;
  const skipping = data.features.length - pending;
  return (
    <div>
      <p className="text-fg">
        Found <strong>{data.totalFeatures}</strong> golf feature{data.totalFeatures === 1 ? '' : 's'} across{' '}
        <strong>{data.holes.length}</strong> hole{data.holes.length === 1 ? '' : 's'}.
      </p>
      <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-fg-muted">
        {Object.entries(data.byZoneType).map(([k, v]) => (
          <li key={k}>
            <span className="font-medium text-fg">{v}</span> {k.replace('_', ' ')}
          </li>
        ))}
      </ul>
      {skipping > 0 && (
        <p className="mt-3 text-xs text-fg-muted">
          {skipping} already imported, will be skipped. {pending} new to insert.
        </p>
      )}
    </div>
  );
}

function CreateZonePanel({
  polygon,
  onSave,
  onCancel,
  holes,
}: {
  polygon: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  onSave: (input: { name: string; zoneType: ZoneType; grassSpecies: string }) => void;
  onCancel: () => void;
  holes: Hole[];
}) {
  const [name, setName] = useState('');
  const [zoneType, setZoneType] = useState<ZoneType>('fairway');
  const [grassSpecies, setGrassSpecies] = useState('');

  return (
    <div className="absolute bottom-4 left-4 right-4 md:right-auto md:w-96 z-10 bg-bg-card rounded-lg shadow-md border border-border p-4">
      <h2 className="font-semibold text-fg">New zone</h2>
      <p className="text-sm text-fg-muted mt-1">
        {polygon ? 'Polygon captured. Name it and save.' : 'Click to drop vertices. Double-click to finish.'}
      </p>

      <div className="mt-3 grid gap-3 text-sm">
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-fg-muted">Zone type</span>
          <select
            value={zoneType}
            onChange={(e) => setZoneType(e.target.value as ZoneType)}
            className="mt-1 w-full border border-border rounded-md py-2 px-2"
          >
            {ZONE_TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {ZONE_TYPE_STYLE[t].label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-fg-muted">
            Name (e.g. Green 7, Fairway 12)
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full border border-border rounded-md py-2 px-2"
            maxLength={80}
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-fg-muted">
            Grass species (optional)
          </span>
          <input
            value={grassSpecies}
            onChange={(e) => setGrassSpecies(e.target.value)}
            className="mt-1 w-full border border-border rounded-md py-2 px-2"
            placeholder={holes[0]?.grassFairway ?? 'e.g. Bentgrass A1/A4'}
          />
        </label>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => onSave({ name, zoneType, grassSpecies })}
          disabled={!polygon || name.length === 0}
          className="px-4 py-2 rounded-md bg-greeniq-500 text-[#0b1014] font-semibold hover:bg-greeniq-400 disabled:opacity-50"
        >
          Save zone
        </button>
        <button onClick={onCancel} className="text-fg-secondary hover:text-fg text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

/** Color-ramp legend for the Absolute view. Shows the actual value scale so
 *  users can tell "green = 0.8 NDVI" apart from "green = 0.4 NDVI". */
function AbsoluteLegend({ index }: { index: SatelliteIndex }) {
  const stops = INDEX_VALUE_STOPS[index];
  // Sample 9 colors across the index's legitimate range for the gradient bar.
  // Keeps the ramp visually honest — no hallucinated colors outside the clamp.
  const lo = index === 'ndwi' ? -0.4 : -0.1;
  const hi = index === 'ndwi' ? 0.6 : 0.9;
  const samples = Array.from({ length: 9 }, (_, i) => {
    const v = lo + ((hi - lo) * i) / 8;
    return colorForValue(index, v);
  });
  const gradient = `linear-gradient(to right, ${samples.join(', ')})`;
  return (
    <div className="bg-bg-card rounded-md shadow-md border border-border p-2 max-w-xs">
      <div className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold">
        {INDEX_META[index].label} value
      </div>
      <div className="mt-1 h-2 rounded" style={{ background: gradient }} />
      <div className="mt-1 flex justify-between text-[10px] text-fg-muted font-mono">
        {stops.map((s) => (
          <span key={s.value}>{s.label}</span>
        ))}
      </div>
    </div>
  );
}

// Primary satellite picker — two pitchable overlays ("Turf Health" + "Moisture")
// with the remaining four technical indices tucked behind an Advanced drawer.
// The old 6-button strip overwhelmed supers; the job of this page is to let
// them see one thing, not toggle every available band.
const PRIMARY_INDICES = [
  { id: 'ndvi' as const, label: 'Turf Health' },
  { id: 'ndwi' as const, label: 'Moisture' },
];
const ADVANCED_INDICES = ['ndre', 'gndvi', 'savi', 'msavi'] as const;

function SatelliteIndexPicker({
  state,
  onSelectIndex,
}: {
  state: Extract<SatelliteModeState, { active: true }>;
  onSelectIndex: (idx: SatelliteIndex) => void;
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const advActive = ADVANCED_INDICES.includes(state.index as (typeof ADVANCED_INDICES)[number]);
  return (
    <div className="bg-bg-card rounded-md shadow-md border border-border p-2 flex flex-wrap gap-1 max-w-xs">
      {PRIMARY_INDICES.map((p) => {
        const meta = INDEX_META[p.id];
        const isActive = state.index === p.id;
        return (
          <button
            key={p.id}
            onClick={() => onSelectIndex(p.id)}
            className={[
              'px-3 py-1.5 text-xs font-semibold rounded min-h-[32px]',
              isActive ? 'text-white' : 'text-fg-secondary hover:bg-bg-hover',
            ].join(' ')}
            style={{ backgroundColor: isActive ? meta.color : undefined }}
            aria-pressed={isActive}
          >
            {p.label}
          </button>
        );
      })}
      <button
        onClick={() => setAdvancedOpen((v) => !v)}
        title="Research-grade diagnostic indices — use when Turf Health or Moisture isn't telling the full story"
        className={[
          'px-2 py-1.5 text-xs font-semibold rounded min-h-[32px]',
          advActive ? 'bg-bg-subtle text-fg' : 'text-fg-muted hover:bg-bg-hover',
        ].join(' ')}
        aria-expanded={advancedOpen}
      >
        Advanced indices {advancedOpen ? '▾' : '▸'}
      </button>
      {advancedOpen && (
        <div className="w-full mt-1 flex flex-wrap gap-1 border-t border-border pt-2">
          {ADVANCED_INDICES.map((idx) => {
            const meta = INDEX_META[idx];
            const isActive = state.index === idx;
            return (
              <button
                key={idx}
                onClick={() => onSelectIndex(idx)}
                className={[
                  'px-2 py-1 text-[10px] font-semibold rounded min-h-[26px]',
                  isActive ? 'text-white' : 'text-fg-secondary hover:bg-bg-hover',
                ].join(' ')}
                style={{ backgroundColor: isActive ? meta.color : undefined }}
                aria-pressed={isActive}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AnomalyLegend({ anomaly }: { anomaly: AnomalyOverviewResponse | null }) {
  if (!anomaly) {
    return (
      <div className="bg-bg-card rounded-md shadow-md border border-border p-2 text-xs text-fg-muted max-w-xs">
        Computing anomaly…
      </div>
    );
  }
  const methods = new Set(anomaly.anomalies.map((a) => a.method));
  const methodLabel =
    methods.has('same_week_of_year')
      ? 'Same week of year'
      : methods.has('rolling_12w')
        ? 'Rolling 12-week'
        : methods.has('rolling_to_date')
          ? 'Rolling to date'
          : 'Insufficient data';
  return (
    <div className="bg-bg-card rounded-md shadow-md border border-border p-2 max-w-xs">
      <div className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold">
        Baseline: {methodLabel}
      </div>
      <div className="mt-1 flex items-center gap-1 text-[10px] text-fg-muted">
        <span>-3σ</span>
        <span
          className="flex-1 h-2 rounded"
          style={{
            background:
              'linear-gradient(to right, #b91c1c, #fca5a5, #e5e7eb, #86efac, #15803d)',
          }}
        />
        <span>+3σ</span>
      </div>
    </div>
  );
}

const SAT_SPEEDS = [0.5, 1, 2] as const;

function SatelliteScrubberOverlay({
  state,
  playing,
  speed,
  onDateChange,
  onPlayingChange,
  onSpeedChange,
  onSelectIndex,
  onSelectView,
}: {
  state: Extract<SatelliteModeState, { active: true }>;
  playing: boolean;
  speed: (typeof SAT_SPEEDS)[number];
  onDateChange: (date: string) => void;
  onPlayingChange: (playing: boolean) => void;
  onSpeedChange: (speed: (typeof SAT_SPEEDS)[number]) => void;
  onSelectIndex: (idx: SatelliteIndex) => void;
  onSelectView: (v: SatelliteView) => void;
}) {
  const dates = state.overview.dates;
  const atEnd = dates.length > 0 && state.date === dates[dates.length - 1];
  const canPlay = dates.length > 1;
  // Consolidated mode panel — all Sat Mode controls live here so the top-right
  // corner stays reserved for passive legends (heatmap) and the description
  // card gets ~2× the horizontal room it had before.
  return (
    <div className="absolute bottom-4 left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-[840px] md:max-w-[calc(100vw-2rem)] z-10 bg-bg-card rounded-lg shadow-md border border-border p-3 flex flex-col gap-2">
      {/* Row 1 — index label + playback controls */}
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-fg-muted min-w-0">
          <span className="font-semibold text-fg">{INDEX_META[state.index].label}</span>
          <span className="ml-2">— {INDEX_META[state.index].tagline}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => {
              if (atEnd && !playing) onDateChange(dates[0]!);
              onPlayingChange(!playing);
            }}
            disabled={!canPlay}
            aria-label={playing ? 'Pause satellite playback' : 'Play satellite playback'}
            className="w-10 h-10 rounded-md bg-greeniq-500 text-[#0b1014] font-bold hover:bg-greeniq-400 disabled:opacity-40 disabled:bg-bg-hover disabled:text-fg-muted"
          >
            {playing ? '❚❚' : '▶'}
          </button>
          <div className="inline-flex rounded-sm border border-border overflow-hidden">
            {SAT_SPEEDS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onSpeedChange(s)}
                className={[
                  'px-2.5 h-8 text-[11px] font-mono transition-colors',
                  s === speed
                    ? 'bg-greeniq-500 text-[#0b1014]'
                    : 'bg-transparent text-fg-muted hover:bg-bg-hover hover:text-fg',
                ].join(' ')}
                aria-pressed={s === speed}
              >
                {s}x
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Row 2 — date scrubber */}
      <TimeScrubber dates={dates} value={state.date} onChange={onDateChange} />

      {/* Row 3 — view mode + index picker */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="bg-bg-elevated rounded-md border border-border p-1 flex gap-1 shrink-0">
          {(['absolute', 'anomaly'] as const).map((v) => (
            <button
              key={v}
              onClick={() => onSelectView(v)}
              className={[
                'px-3 py-1.5 text-xs font-semibold rounded min-h-[32px]',
                state.view === v ? 'bg-greeniq-500 text-[#0b1014]' : 'text-fg-secondary hover:bg-bg-hover',
              ].join(' ')}
              aria-pressed={state.view === v}
            >
              {v === 'absolute' ? 'Absolute' : 'Anomaly (z)'}
            </button>
          ))}
        </div>
        <SatelliteIndexPicker state={state} onSelectIndex={onSelectIndex} />
      </div>

      {/* Row 4 — description (full width, readable) */}
      <div className="rounded-md border border-border bg-bg-elevated px-3 py-2 text-[11px] leading-snug text-fg-muted">
        <span className="font-semibold text-fg">{INDEX_META[state.index].label}</span>
        <span className="ml-1">— {INDEX_DESCRIPTIONS[state.index]}</span>
      </div>

      {/* Row 5 — legend (color ramp) */}
      {state.view === 'anomaly' ? (
        <AnomalyLegend anomaly={state.anomaly} />
      ) : (
        <AbsoluteLegend index={state.index} />
      )}
    </div>
  );
}

interface MapToolbarProps {
  courseSlug: string;
  courseName: string;
  zoneCount: number;
  totalAcres: string;
  basemap: Basemap;
  onBasemapChange: (b: Basemap) => void;
  showZones: boolean;
  onToggleZones: () => void;
  sensorCount: number;
  showSensors: boolean;
  onToggleSensors: () => void;
  showHeatmap: boolean;
  heatmapDisabled: boolean;
  onToggleHeatmap: () => void;
  satelliteActive: boolean;
  onToggleSatelliteMode: () => void;
  timelapseActive: boolean;
  onToggleTimelapse: () => void;
}

const BASEMAP_LABELS: Record<Basemap, string> = {
  'satellite-streets': 'Sat + Labels',
  satellite: 'Satellite',
  streets: 'Streets',
};

function MapToolbar(p: MapToolbarProps) {
  return (
    <div className="absolute top-4 left-4 right-4 z-10 bg-bg-card rounded-lg shadow-md border border-border px-3 py-2 flex items-center gap-3 flex-wrap">
      {/* Exit + course identity */}
      <Link
        to={`/app/${p.courseSlug}`}
        className="shrink-0 px-3 py-1.5 rounded-md bg-bg-hover text-greeniq-300 text-xs font-semibold hover:bg-bg-elevated"
        aria-label="Close map and return to overview"
      >
        ← Overview
      </Link>
      <div className="min-w-0 pr-2 border-r border-border">
        <p className="text-[10px] uppercase tracking-wider text-greeniq-400 font-semibold leading-none">
          Course Map
        </p>
        <div className="mt-0.5 text-sm font-display font-bold text-fg truncate max-w-[240px]">
          {p.courseName}
        </div>
        <p className="text-[10px] text-fg-muted leading-tight">
          {p.zoneCount} zones · {p.totalAcres} ac
        </p>
      </div>

      {/* Layer toggles */}
      <ToolbarButton
        active={p.showZones}
        onClick={p.onToggleZones}
        label={`${p.showZones ? 'Hide' : 'Show'} zones (${p.zoneCount})`}
      />
      {p.sensorCount > 0 && (
        <ToolbarButton
          active={p.showSensors}
          onClick={p.onToggleSensors}
          label={`${p.showSensors ? 'Hide' : 'Show'} sensors (${p.sensorCount})`}
        />
      )}
      {!p.heatmapDisabled && (
        <ToolbarButton
          active={p.showHeatmap}
          onClick={p.onToggleHeatmap}
          label={`${p.showHeatmap ? 'Hide' : 'Show'} heatmap`}
        />
      )}

      <div className="h-6 w-px bg-greeniq-500/10 hidden md:block" />

      {/* Basemap */}
      <div className="rounded-md border border-border overflow-hidden flex">
        {(Object.keys(BASEMAP_LABELS) as Basemap[]).map((b) => (
          <button
            key={b}
            onClick={() => p.onBasemapChange(b)}
            className={[
              'px-2.5 py-1.5 text-xs font-semibold',
              p.basemap === b ? 'bg-greeniq-500 text-[#0b1014]' : 'text-fg-secondary hover:bg-bg-hover',
            ].join(' ')}
          >
            {BASEMAP_LABELS[b]}
          </button>
        ))}
      </div>

      <div className="h-6 w-px bg-greeniq-500/10 hidden md:block" />

      <ToolbarButton
        active={p.satelliteActive}
        onClick={p.onToggleSatelliteMode}
        label={p.satelliteActive ? 'Exit Sat Mode' : 'Sat Mode'}
      />
      <ToolbarButton
        active={p.timelapseActive}
        onClick={p.onToggleTimelapse}
        label={p.timelapseActive ? 'Exit Timelapse' : 'Timelapse'}
      />
    </div>
  );
}

function ToolbarButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'px-3 py-1.5 text-xs font-semibold rounded-md border whitespace-nowrap',
        active
          ? 'bg-greeniq-500 text-[#0b1014] border-greeniq-800'
          : 'bg-bg-card text-fg-secondary border-border hover:bg-bg-hover',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

const ZONE_SOURCE_ID = 'course-zones';
const ZONE_FILL_LAYER = 'course-zones-fill';
const ZONE_OUTLINE_LAYER = 'course-zones-outline';

const SENSOR_SOURCE_ID = 'course-sensors';
const SENSOR_LAYER_ID = 'course-sensors-dots';
const SENSOR_HEATMAP_LAYER_ID = 'course-sensors-heatmap';

// VWC thresholds match services/sensors/alerts.ts so a red dot always
// corresponds to a critical alert and an amber dot to a warn.
function sensorDotColor(vwc: number | null): string {
  if (vwc === null || !Number.isFinite(vwc)) return '#9ca3af'; // slate-400 — unknown
  if (vwc < 0.10) return '#dc2626'; // red-600 — critical
  if (vwc < 0.13) return '#f59e0b'; // amber-500 — warn
  if (vwc > 0.35) return '#2563eb'; // blue-600 — saturated
  return '#16a34a'; // green-600 — healthy
}

function renderSensors(
  map: MapboxMap,
  placements: PlacedSensor[],
  highlightId: number | null = null,
) {
  const features: GeoJSON.Feature[] = placements.map((p) => ({
    type: 'Feature',
    id: p.id,
    properties: {
      id: p.id,
      zoneId: p.zoneId,
      type: p.type,
      depthInches: p.depthInches,
      vwc: p.latestVwc,
      color: sensorDotColor(p.latestVwc),
      highlighted: p.id === highlightId,
    },
    geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
  }));
  const collection: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features };
  const existing = map.getSource(SENSOR_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
  if (existing) {
    existing.setData(collection);
    return;
  }
  map.addSource(SENSOR_SOURCE_ID, { type: 'geojson', data: collection });
  // Ambient sensor-density heatmap (drawn BENEATH the dot layer). Weights
  // each sensor by VWC-danger so critical/dry areas glow red while healthy
  // areas fade to a calm green wash. Visibility toggled by the Show heatmap
  // button via a separate useEffect — starts hidden so the page doesn't
  // flash a heatmap on first paint.
  map.addLayer({
    id: SENSOR_HEATMAP_LAYER_ID,
    source: SENSOR_SOURCE_ID,
    type: 'heatmap',
    layout: { visibility: 'none' },
    paint: {
      // Weight: low VWC (dry) = max weight → red. Healthy = 0.3. Saturated
      // = moderate so it still shows as a cool hotspot.
      'heatmap-weight': [
        'interpolate', ['linear'], ['get', 'vwc'],
        0.00, 1.0,
        0.10, 0.9,
        0.13, 0.7,
        0.20, 0.3,
        0.35, 0.5,
        0.50, 0.7,
      ],
      // Radius scales with zoom so the blobs bleed softly at course view
      // and tighten up when you zoom in on a single green.
      'heatmap-radius': [
        'interpolate', ['linear'], ['zoom'],
        12, 30,
        15, 55,
        18, 45,
        20, 30,
      ],
      // Color ramp by density. Red hot = critical clustering; green ambient
      // in healthy zones; transparent where no sensors are.
      'heatmap-color': [
        'interpolate', ['linear'], ['heatmap-density'],
        0.0, 'rgba(0,0,0,0)',
        0.15, 'rgba(34, 163, 74, 0.25)',
        0.40, 'rgba(34, 163, 74, 0.55)',
        0.60, 'rgba(245, 158, 11, 0.55)',
        0.80, 'rgba(220, 38, 38, 0.65)',
        1.00, 'rgba(220, 38, 38, 0.85)',
      ],
      // Boost intensity at lower zooms so coverage reads at course-overview.
      'heatmap-intensity': [
        'interpolate', ['linear'], ['zoom'],
        12, 1.2,
        18, 0.8,
        20, 0.4,
      ],
      // Fade out at high zoom so individual dot VWC values stay legible.
      'heatmap-opacity': [
        'interpolate', ['linear'], ['zoom'],
        12, 0.9,
        17, 0.7,
        19, 0.25,
      ],
    },
  });
  map.addLayer({
    id: SENSOR_LAYER_ID,
    source: SENSOR_SOURCE_ID,
    type: 'circle',
    paint: {
      // Mapbox requires zoom-dependent expressions at the TOP level of a
      // paint property — `['zoom']` nested inside a `case` fails validation
      // silently-to-the-user ("may only be used as input to a top-level
      // step/interpolate") and the addLayer throws, leaving the source on
      // the map but the layer missing. Structure with interpolate at top,
      // per-stop `case` for the highlighted branch.
      'circle-radius': [
        'interpolate', ['linear'], ['zoom'],
        12, ['case', ['==', ['get', 'highlighted'], true], 7, 2.5],
        15, ['case', ['==', ['get', 'highlighted'], true], 8, 4],
        18, ['case', ['==', ['get', 'highlighted'], true], 10, 6],
        20, ['case', ['==', ['get', 'highlighted'], true], 12, 8],
      ],
      'circle-color': ['get', 'color'],
      'circle-stroke-color': ['case', ['==', ['get', 'highlighted'], true], '#2563eb', '#ffffff'],
      'circle-stroke-width': ['case', ['==', ['get', 'highlighted'], true], 2, 0.75],
      'circle-opacity': 0.95,
    },
  });
  map.on('mouseenter', SENSOR_LAYER_ID, (e) => {
    map.getCanvas().style.cursor = 'pointer';
    const f = e.features?.[0];
    if (!f) return;
    const vwc = (f.properties as { vwc?: number | null }).vwc;
    const depth = (f.properties as { depthInches?: number | null }).depthInches;
    const id = (f.properties as { id?: number }).id ?? '?';
    const label =
      vwc !== null && typeof vwc === 'number'
        ? `Sensor #${id} · ${depth ?? '?'}″ · VWC ${vwc.toFixed(3)}`
        : `Sensor #${id} · awaiting tick`;
    const coords = (f.geometry as GeoJSON.Point).coordinates.slice() as [number, number];
    new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 10 })
      .setLngLat(coords)
      .setText(label)
      .addTo(map);
  });
  map.on('mouseleave', SENSOR_LAYER_ID, () => {
    map.getCanvas().style.cursor = '';
    document.querySelectorAll('.mapboxgl-popup').forEach((el) => el.remove());
  });
}

function renderZones(
  map: MapboxMap,
  zones: Zone[],
  onZoneClick: (id: number) => void,
  fillOverride: Record<number, string> | null = null,
) {
  const features = zones.map((z) => ({
    type: 'Feature' as const,
    id: z.id,
    properties: {
      id: z.id,
      name: z.name,
      zoneType: z.zoneType,
      fillColor: fillOverride?.[z.id] ?? ZONE_TYPE_STYLE[z.zoneType].fill,
      outlineColor: ZONE_TYPE_STYLE[z.zoneType].outline,
    },
    geometry: z.polygon as GeoJSON.Polygon | GeoJSON.MultiPolygon,
  }));
  const collection: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features };
  const existing = map.getSource(ZONE_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
  if (existing) {
    existing.setData(collection);
    return;
  }
  map.addSource(ZONE_SOURCE_ID, { type: 'geojson', data: collection });
  map.addLayer({
    id: ZONE_FILL_LAYER,
    source: ZONE_SOURCE_ID,
    type: 'fill',
    paint: { 'fill-color': ['get', 'fillColor'], 'fill-opacity': 0.55 },
  });
  map.addLayer({
    id: ZONE_OUTLINE_LAYER,
    source: ZONE_SOURCE_ID,
    type: 'line',
    paint: { 'line-color': ['get', 'outlineColor'], 'line-width': 2 },
  });
  map.on('click', ZONE_FILL_LAYER, (e) => {
    const feature = e.features?.[0];
    const rawId = feature?.properties?.id;
    const id = typeof rawId === 'number' ? rawId : Number(rawId);
    if (Number.isFinite(id)) onZoneClick(id);
  });
  map.on('mouseenter', ZONE_FILL_LAYER, () => {
    map.getCanvas().style.cursor = 'pointer';
  });
  map.on('mouseleave', ZONE_FILL_LAYER, () => {
    map.getCanvas().style.cursor = '';
  });
}
